import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Dumbbell,
  Users,
  BarChart3,
  Settings,
  Plus,
  Trash2,
  Edit,
  DollarSign,
  ChevronRight,
  Upload,
  Palette,
  Link as LinkIcon,
  Activity,
  CreditCard,
  ExternalLink,
  Check,
  AlertCircle,
  Key
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InfluencerDashboard = () => {
  const { accessCode } = useParams();
  const [profile, setProfile] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Stripe state
  const [stripeStatus, setStripeStatus] = useState(null);
  const [manualKeyInput, setManualKeyInput] = useState("");
  const [stripeLoading, setStripeLoading] = useState(false);

  // Workout form state
  const [workoutOpen, setWorkoutOpen] = useState(false);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [workoutForm, setWorkoutForm] = useState({
    name: "",
    description: "",
    category: "General",
    duration_minutes: 45,
    difficulty: "Intermediate",
    exercises: []
  });

  // User detail state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [nutritionForm, setNutritionForm] = useState({
    name: "",
    calories: 2000,
    protein: 150,
    carbs: 200,
    fats: 70,
    meals: [],
    notes: ""
  });

  const headers = { "X-Access-Code": accessCode };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profRes, analyticsRes, workoutsRes, usersRes] = await Promise.all([
        axios.get(`${API}/influencer/profile`, { headers }),
        axios.get(`${API}/influencer/analytics`, { headers }),
        axios.get(`${API}/influencer/workouts`, { headers }),
        axios.get(`${API}/influencer/users`, { headers })
      ]);
      setProfile(profRes.data);
      setAnalytics(analyticsRes.data);
      setWorkouts(workoutsRes.data);
      setUsers(usersRes.data);
      
      // Fetch Stripe status
      fetchStripeStatus();
    } catch (error) {
      toast.error("Error loading data or invalid access code");
    }
    setLoading(false);
  };
  
  const fetchStripeStatus = async () => {
    try {
      const res = await axios.get(`${API}/influencer/stripe/status`, { headers });
      setStripeStatus(res.data);
    } catch (error) {
      console.error("Error fetching Stripe status");
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessCode]);
  
  // Stripe Connect
  const handleStripeConnect = async () => {
    setStripeLoading(true);
    try {
      const res = await axios.post(`${API}/influencer/stripe/connect`, {
        return_url: `${window.location.origin}/influencer/${accessCode}?stripe=success`,
        refresh_url: `${window.location.origin}/influencer/${accessCode}?stripe=refresh`
      }, { headers });
      
      // Redirigir a Stripe Onboarding
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error connecting Stripe");
    }
    setStripeLoading(false);
  };
  
  // Manual Stripe Key
  const handleSaveManualKey = async () => {
    if (!manualKeyInput || !manualKeyInput.startsWith("sk_")) {
      toast.error("Invalid Stripe API key format (must start with sk_)");
      return;
    }
    setStripeLoading(true);
    try {
      await axios.post(`${API}/influencer/stripe/manual-key?stripe_key=${encodeURIComponent(manualKeyInput)}`, {}, { headers });
      toast.success("Stripe API key saved!");
      setManualKeyInput("");
      fetchStripeStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error saving key");
    }
    setStripeLoading(false);
  };
  
  const handleRemoveManualKey = async () => {
    setStripeLoading(true);
    try {
      await axios.delete(`${API}/influencer/stripe/manual-key`, { headers });
      toast.success("Stripe key removed");
      fetchStripeStatus();
    } catch (error) {
      toast.error("Error removing key");
    }
    setStripeLoading(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/influencer/upload-logo`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      fetchData();
      toast.success("Logo uploaded");
    } catch (error) {
      toast.error("Error uploading logo");
    }
  };

  const updateProfile = async (data) => {
    try {
      await axios.patch(`${API}/influencer/profile`, data, { headers });
      fetchData();
      toast.success("Profile updated");
    } catch (error) {
      toast.error("Error updating profile");
    }
  };

  const addExercise = () => {
    setWorkoutForm({
      ...workoutForm,
      exercises: [
        ...workoutForm.exercises,
        { name: "", video_url: "", sets: 3, reps: 10, rest_time: 60, description: "" }
      ]
    });
  };

  const updateExercise = (index, field, value) => {
    const updated = [...workoutForm.exercises];
    updated[index][field] = value;
    setWorkoutForm({ ...workoutForm, exercises: updated });
  };

  const removeExercise = (index) => {
    const updated = workoutForm.exercises.filter((_, i) => i !== index);
    setWorkoutForm({ ...workoutForm, exercises: updated });
  };

  const handleWorkoutSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingWorkout) {
        await axios.put(`${API}/influencer/workouts/${editingWorkout.id}`, workoutForm, { headers });
        toast.success("Workout updated");
      } else {
        await axios.post(`${API}/influencer/workouts`, workoutForm, { headers });
        toast.success("Workout created");
      }
      setWorkoutOpen(false);
      setEditingWorkout(null);
      setWorkoutForm({
        name: "",
        description: "",
        category: "General",
        duration_minutes: 45,
        difficulty: "Intermediate",
        exercises: []
      });
      fetchData();
    } catch (error) {
      toast.error("Error saving workout");
    }
  };

  const deleteWorkout = async (id) => {
    if (!window.confirm("Delete this workout?")) return;
    try {
      await axios.delete(`${API}/influencer/workouts/${id}`, { headers });
      fetchData();
      toast.success("Workout deleted");
    } catch (error) {
      toast.error("Error deleting workout");
    }
  };

  const openEditWorkout = (workout) => {
    setEditingWorkout(workout);
    setWorkoutForm({
      name: workout.name,
      description: workout.description || "",
      category: workout.category,
      duration_minutes: workout.duration_minutes,
      difficulty: workout.difficulty,
      exercises: workout.exercises || []
    });
    setWorkoutOpen(true);
  };

  const viewUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/influencer/users/${userId}`, { headers });
      setSelectedUser(res.data);
      if (res.data.nutrition_plan) {
        setNutritionForm(res.data.nutrition_plan);
      } else {
        setNutritionForm({
          name: "Custom Plan",
          calories: 2000,
          protein: 150,
          carbs: 200,
          fats: 70,
          meals: [],
          notes: ""
        });
      }
      setUserDetailOpen(true);
    } catch (error) {
      toast.error("Error loading user details");
    }
  };

  const saveNutritionPlan = async () => {
    try {
      await axios.post(
        `${API}/influencer/nutrition`,
        { ...nutritionForm, user_id: selectedUser.user.id },
        { headers }
      );
      toast.success("Nutrition plan saved");
    } catch (error) {
      toast.error("Error saving nutrition plan");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-vexl-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-2 border-vexl-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-vexl-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-vexl-bg flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-white mb-2">Invalid Access Code</p>
          <p className="text-vexl-text-secondary">Please check your access code and try again.</p>
        </div>
      </div>
    );
  }

  const brandColor = profile.custom_color || "#CDF22B";

  return (
    <div className="min-h-screen bg-vexl-bg">
      {/* Header */}
      <header className="bg-vexl-surface border-b border-vexl-border px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {profile.logo_url ? (
              <img
               src={profile.logo_url}
               alt="Logo"
                className="w-10 h-10 object-cover"
              />
            ) : (
              <Dumbbell className="w-8 h-8" style={{ color: brandColor }} />
            )}
            <div>
              <h1 className="font-mono text-xl font-bold text-white">{profile.name}</h1>
              <p className="text-xs text-vexl-text-secondary">/{profile.brand_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="badge badge-success">{profile.subscription_status}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-vexl-surface border border-vexl-border p-1">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-vexl-primary data-[state=active]:text-vexl-bg">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="workouts" className="data-[state=active]:bg-vexl-primary data-[state=active]:text-vexl-bg">
              <Dumbbell className="w-4 h-4 mr-2" />
              Workouts
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-vexl-primary data-[state=active]:text-vexl-bg">
              <Users className="w-4 h-4 mr-2" />
              Users CRM
            </TabsTrigger>
            <TabsTrigger value="branding" className="data-[state=active]:bg-vexl-primary data-[state=active]:text-vexl-bg">
              <Settings className="w-4 h-4 mr-2" />
              Branding
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6" data-testid="dashboard-tab">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="metric-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="metric-value" style={{ color: brandColor }}>{analytics?.total_users || 0}</p>
                    <p className="metric-label">Total Users</p>
                  </div>
                  <Users className="w-10 h-10 opacity-30" style={{ color: brandColor }} />
                </div>
              </div>
              <div className="metric-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="metric-value" style={{ color: brandColor }}>{analytics?.paid_users || 0}</p>
                    <p className="metric-label">Paid Users</p>
                  </div>
                  <DollarSign className="w-10 h-10 opacity-30" style={{ color: brandColor }} />
                </div>
              </div>
              <div className="metric-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="metric-value" style={{ color: brandColor }}>{analytics?.total_workouts || 0}</p>
                    <p className="metric-label">Workouts</p>
                  </div>
                  <Dumbbell className="w-10 h-10 opacity-30" style={{ color: brandColor }} />
                </div>
              </div>
              <div className="metric-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="metric-value" style={{ color: brandColor }}>${analytics?.monthly_revenue || 0}</p>
                    <p className="metric-label">Monthly Revenue</p>
                  </div>
                  <Activity className="w-10 h-10 opacity-30" style={{ color: brandColor }} />
                </div>
              </div>
            </div>

            {/* Recent Users */}
            <div className="card-dark p-6">
              <h3 className="font-mono text-lg font-semibold text-white mb-4">Recent Users</h3>
              {analytics?.recent_users?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.recent_users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-vexl-bg border border-vexl-border hover:border-vexl-primary/30 transition-colors cursor-pointer"
                      onClick={() => viewUserDetail(user.id)}
                    >
                      <div>
                        <p className="font-medium text-white">{user.name}</p>
                        <p className="text-sm text-vexl-text-secondary">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`badge ${user.subscription_status === "paid" ? "badge-success" : "badge-warning"}`}>
                          {user.subscription_status}
                        </span>
                        <ChevronRight className="w-4 h-4 text-vexl-text-secondary" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-vexl-text-secondary">No users yet</p>
              )}
            </div>

            {/* Access Info */}
            <div className="card-dark p-6">
              <h3 className="font-mono text-lg font-semibold text-white mb-4">Access Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-vexl-bg border border-vexl-border">
                  <p className="text-sm text-vexl-text-secondary mb-1">Client App URL</p>
                  <p className="font-mono text-sm" style={{ color: brandColor }}>
                    {window.location.origin}/{profile.brand_name}
                  </p>
                </div>
                <div className="p-4 bg-vexl-bg border border-vexl-border">
                  <p className="text-sm text-vexl-text-secondary mb-1">User Registration Code</p>
                  <p className="font-mono text-sm" style={{ color: brandColor }}>{profile.user_access_code}</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Workouts Tab */}
          <TabsContent value="workouts" className="space-y-6" data-testid="workouts-tab">
            <div className="flex items-center justify-between">
              <h2 className="font-mono text-xl font-semibold text-white">Workout Library</h2>
              <Button
                data-testid="create-workout-btn"
                onClick={() => {
                  setEditingWorkout(null);
                  setWorkoutForm({
                    name: "",
                    description: "",
                    category: "General",
                    duration_minutes: 45,
                    difficulty: "Intermediate",
                    exercises: []
                  });
                  setWorkoutOpen(true);
                }}
                className="bg-vexl-primary text-vexl-bg hover:bg-vexl-primary-hover"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Workout
              </Button>
            </div>

            {workouts.length === 0 ? (
              <div className="card-dark p-12 text-center">
                <Dumbbell className="w-12 h-12 mx-auto mb-4" style={{ color: brandColor }} />
                <p className="text-vexl-text-secondary">No workouts yet. Create your first one!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {workouts.map((workout) => (
                  <div key={workout.id} className="workout-card" data-testid={`workout-card-${workout.id}`}>
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-mono font-semibold text-white">{workout.name}</h3>
                          <p className="text-sm text-vexl-text-secondary">{workout.category}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditWorkout(workout)}
                            className="text-vexl-text-secondary hover:text-vexl-primary"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteWorkout(workout.id)}
                            className="text-vexl-text-secondary hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-vexl-text-secondary mb-4 line-clamp-2">
                        {workout.description || "No description"}
                      </p>
                      <div className="flex items-center gap-4 text-sm">
                        <span style={{ color: brandColor }}>{workout.duration_minutes} min</span>
                        <span className="text-vexl-text-secondary">{workout.difficulty}</span>
                        <span className="text-vexl-text-secondary">{workout.exercises?.length || 0} exercises</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Users CRM Tab */}
          <TabsContent value="users" className="space-y-6" data-testid="users-tab">
            <h2 className="font-mono text-xl font-semibold text-white">User Management</h2>
            
            {users.length === 0 ? (
              <div className="card-dark p-12 text-center">
                <Users className="w-12 h-12 mx-auto mb-4" style={{ color: brandColor }} />
                <p className="text-vexl-text-secondary">No users registered yet.</p>
                <p className="text-sm text-vexl-text-secondary mt-2">
                  Share your registration code: <span className="font-mono" style={{ color: brandColor }}>{profile.user_access_code}</span>
                </p>
              </div>
            ) : (
              <div className="card-dark overflow-hidden">
                <table className="table-dark">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} data-testid={`user-row-${user.id}`}>
                        <td className="font-medium text-white">{user.name}</td>
                        <td className="text-vexl-text-secondary">{user.email}</td>
                        <td>
                          <span className={`badge ${user.subscription_status === "paid" ? "badge-success" : "badge-warning"}`}>
                            {user.subscription_status}
                          </span>
                        </td>
                        <td className="text-vexl-text-secondary text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewUserDetail(user.id)}
                            className="text-vexl-text-secondary hover:text-vexl-primary"
                          >
                            View Details
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding" className="space-y-6" data-testid="branding-tab">
            <h2 className="font-display text-xl font-semibold text-white">Brand & Payment Settings</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Logo Upload */}
              <div className="card-dark p-6">
                <h3 className="font-display font-semibold text-white mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5" style={{ color: brandColor }} />
                  Logo
                </h3>
                <div className="flex items-center gap-6">
                  <div className="w-24 h-24 bg-vexl-bg border border-vexl-border flex items-center justify-center overflow-hidden rounded-xl">
                    {profile.logo_url ? (
                      <img
                       src={profile.logo_url}
                       alt="Logo"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Dumbbell className="w-10 h-10 text-vexl-text-secondary" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="btn-outline cursor-pointer inline-block text-sm"
                    >
                      Upload Logo
                    </label>
                    <p className="text-xs text-vexl-text-secondary mt-2">
                      Recommended: 200x200px, PNG or JPG
                    </p>
                  </div>
                </div>
              </div>

              {/* Brand Color */}
              <div className="card-dark p-6">
                <h3 className="font-display font-semibold text-white mb-4 flex items-center gap-2">
                  <Palette className="w-5 h-5" style={{ color: brandColor }} />
                  Accent Color
                </h3>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    value={profile.custom_color || "#CDF22B"}
                    onChange={(e) => updateProfile({ custom_color: e.target.value })}
                    className="w-16 h-16 cursor-pointer border-0 bg-transparent rounded-xl"
                  />
                  <div>
                    <p className="font-display text-white">{profile.custom_color || "#CDF22B"}</p>
                    <p className="text-xs text-vexl-text-secondary">
                      This color will be used throughout your client app
                    </p>
                  </div>
                </div>
              </div>

              {/* Subscription Price */}
              <div className="card-dark p-6">
                <h3 className="font-display font-semibold text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" style={{ color: brandColor }} />
                  Subscription Price
                </h3>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    value={profile.subscription_price || 29.99}
                    onChange={(e) => updateProfile({ subscription_price: parseFloat(e.target.value) })}
                    className="input-dark w-32"
                    min="0"
                    step="0.01"
                  />
                  <span className="text-vexl-text-secondary">USD / month</span>
                </div>
              </div>

              {/* Brand URL */}
              <div className="card-dark p-6">
                <h3 className="font-display font-semibold text-white mb-4 flex items-center gap-2">
                  <LinkIcon className="w-5 h-5" style={{ color: brandColor }} />
                  Your URL
                </h3>
                <div className="p-4 bg-vexl-bg border border-vexl-border rounded-xl">
                  <p className="font-display" style={{ color: brandColor }}>
                    {window.location.origin}/{profile.brand_name}
                  </p>
                </div>
              </div>
            </div>
            
            {/* STRIPE PAYMENT CONFIGURATION */}
            <div className="mt-8">
              <h2 className="font-display text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <CreditCard className="w-6 h-6" style={{ color: brandColor }} />
                Payment Configuration
              </h2>
              
              {/* Payment Status */}
              <div className="card-dark p-6 mb-6">
                <h3 className="font-display font-semibold text-white mb-4">Payment Status</h3>
                <div className="flex items-center gap-4">
                  {stripeStatus?.can_receive_payments ? (
                    <div className="flex items-center gap-2 text-green-400">
                      <Check className="w-5 h-5" />
                      <span>Ready to receive payments</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-yellow-400">
                      <AlertCircle className="w-5 h-5" />
                      <span>Configure Stripe to receive payments</span>
                    </div>
                  )}
                </div>
                
                {stripeStatus && (
                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div className="p-3 bg-vexl-bg rounded-lg">
                      <p className="text-vexl-text-secondary">Stripe Connect</p>
                      <p className={stripeStatus.stripe_connect_complete ? "text-green-400" : "text-yellow-400"}>
                        {stripeStatus.stripe_connect_complete ? "Connected" : stripeStatus.has_stripe_connect ? "Pending" : "Not connected"}
                      </p>
                    </div>
                    <div className="p-3 bg-vexl-bg rounded-lg">
                      <p className="text-vexl-text-secondary">Manual API Key</p>
                      <p className={stripeStatus.has_manual_key ? "text-green-400" : "text-vexl-text-secondary"}>
                        {stripeStatus.has_manual_key ? "Configured" : "Not configured"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Option 1: Stripe Connect */}
                <div className="card-dark p-6 border-2 border-[#1E45FB]/30">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 bg-[#1E45FB] rounded-full flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-white">Stripe Connect</h3>
                      <p className="text-xs text-vexl-text-secondary">Recommended</p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-vexl-text-secondary mb-4">
                    Connect your Stripe account directly. You'll receive payments instantly with a small platform fee ({profile.platform_fee_percent || 5}%).
                  </p>
                  
                  <ul className="text-sm text-vexl-text-secondary space-y-2 mb-6">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      Instant payouts to your bank
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      Automatic tax reporting
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      No need to share API keys
                    </li>
                  </ul>
                  
                  {stripeStatus?.stripe_connect_complete ? (
                    <div className="flex items-center gap-2 text-green-400">
                      <Check className="w-5 h-5" />
                      <span>Account connected</span>
                    </div>
                  ) : (
                    <Button
                      onClick={handleStripeConnect}
                      disabled={stripeLoading}
                      className="w-full bg-[#1E45FB] text-white hover:bg-[#1738d4] rounded-full"
                    >
                      {stripeLoading ? "Loading..." : stripeStatus?.has_stripe_connect ? "Continue Setup" : "Connect Stripe Account"}
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
                
                {/* Option 2: Manual API Key */}
                <div className="card-dark p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 bg-vexl-surface rounded-full flex items-center justify-center border border-vexl-border">
                      <Key className="w-5 h-5 text-vexl-text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-white">Manual API Key</h3>
                      <p className="text-xs text-vexl-text-secondary">Alternative</p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-vexl-text-secondary mb-4">
                    Provide your own Stripe Secret Key. All payments go directly to your account with no platform fees.
                  </p>
                  
                  {stripeStatus?.has_manual_key ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-green-400">
                        <Check className="w-5 h-5" />
                        <span>API Key configured</span>
                      </div>
                      <Button
                        onClick={handleRemoveManualKey}
                        disabled={stripeLoading}
                        variant="outline"
                        className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-full"
                      >
                        Remove API Key
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-vexl-text-secondary mb-1 block">Stripe Secret Key</label>
                        <Input
                          type="password"
                          placeholder="sk_live_..."
                          value={manualKeyInput}
                          onChange={(e) => setManualKeyInput(e.target.value)}
                          className="input-dark"
                        />
                        <p className="text-xs text-vexl-text-secondary mt-1">
                          Find your key in <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-[#1E45FB] underline">Stripe Dashboard</a>
                        </p>
                      </div>
                      <Button
                        onClick={handleSaveManualKey}
                        disabled={stripeLoading || !manualKeyInput}
                        className="w-full bg-[#CDF22B] text-vexl-bg hover:bg-[#b8dc1f] rounded-full"
                      >
                        {stripeLoading ? "Validating..." : "Save API Key"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Warning if no payment method */}
              {!stripeStatus?.can_receive_payments && (
                <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                    <div>
                      <p className="text-yellow-400 font-medium">Payment not configured</p>
                      <p className="text-sm text-vexl-text-secondary mt-1">
                        Until you configure a payment method, subscriptions will be processed through the platform account. 
                        Connect Stripe to receive payments directly to your account.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Workout Dialog */}
      <Dialog open={workoutOpen} onOpenChange={setWorkoutOpen}>
        <DialogContent className="bg-vexl-surface border-vexl-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-white">
              {editingWorkout ? "Edit Workout" : "Create Workout"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleWorkoutSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-vexl-text-secondary mb-1 block">Name</label>
                <Input
                  value={workoutForm.name}
                  onChange={(e) => setWorkoutForm({ ...workoutForm, name: e.target.value })}
                  className="input-dark"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-vexl-text-secondary mb-1 block">Category</label>
                <Select
                  value={workoutForm.category}
                  onValueChange={(v) => setWorkoutForm({ ...workoutForm, category: v })}
                >
                  <SelectTrigger className="input-dark">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-vexl-surface border-vexl-border">
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="Strength">Strength</SelectItem>
                    <SelectItem value="Cardio">Cardio</SelectItem>
                    <SelectItem value="HIIT">HIIT</SelectItem>
                    <SelectItem value="Flexibility">Flexibility</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm text-vexl-text-secondary mb-1 block">Description</label>
              <Textarea
                value={workoutForm.description}
                onChange={(e) => setWorkoutForm({ ...workoutForm, description: e.target.value })}
                className="input-dark min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-vexl-text-secondary mb-1 block">Duration (min)</label>
                <Input
                  type="number"
                  value={workoutForm.duration_minutes}
                  onChange={(e) => setWorkoutForm({ ...workoutForm, duration_minutes: parseInt(e.target.value) })}
                  className="input-dark"
                />
              </div>
              <div>
                <label className="text-sm text-vexl-text-secondary mb-1 block">Difficulty</label>
                <Select
                  value={workoutForm.difficulty}
                  onValueChange={(v) => setWorkoutForm({ ...workoutForm, difficulty: v })}
                >
                  <SelectTrigger className="input-dark">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-vexl-surface border-vexl-border">
                    <SelectItem value="Beginner">Beginner</SelectItem>
                    <SelectItem value="Intermediate">Intermediate</SelectItem>
                    <SelectItem value="Advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Exercises */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-vexl-text-secondary">Exercises</label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={addExercise}
                  className="text-vexl-primary hover:text-vexl-primary-hover"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Exercise
                </Button>
              </div>
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {workoutForm.exercises.map((ex, i) => (
                  <div key={i} className="p-4 bg-vexl-bg border border-vexl-border space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-mono text-vexl-text-secondary">Exercise {i + 1}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeExercise(i)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <Input
                      placeholder="Exercise name"
                      value={ex.name}
                      onChange={(e) => updateExercise(i, "name", e.target.value)}
                      className="input-dark"
                    />
                    <Input
                      placeholder="Video URL (YouTube/Vimeo)"
                      value={ex.video_url}
                      onChange={(e) => updateExercise(i, "video_url", e.target.value)}
                      className="input-dark"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs text-vexl-text-secondary">Sets</label>
                        <Input
                          type="number"
                          value={ex.sets}
                          onChange={(e) => updateExercise(i, "sets", parseInt(e.target.value))}
                          className="input-dark"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-vexl-text-secondary">Reps</label>
                        <Input
                          type="number"
                          value={ex.reps}
                          onChange={(e) => updateExercise(i, "reps", parseInt(e.target.value))}
                          className="input-dark"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-vexl-text-secondary">Rest (sec)</label>
                        <Input
                          type="number"
                          value={ex.rest_time}
                          onChange={(e) => updateExercise(i, "rest_time", parseInt(e.target.value))}
                          className="input-dark"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-vexl-primary text-vexl-bg hover:bg-vexl-primary-hover"
            >
              {editingWorkout ? "Update Workout" : "Create Workout"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
        <DialogContent className="bg-vexl-surface border-vexl-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-white">
              {selectedUser?.user?.name} - Details
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6 mt-4">
              {/* User Info */}
              <div className="p-4 bg-vexl-bg border border-vexl-border">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-vexl-text-secondary">Email</p>
                    <p className="text-white">{selectedUser.user.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-vexl-text-secondary">Status</p>
                    <span className={`badge ${selectedUser.user.subscription_status === "paid" ? "badge-success" : "badge-warning"}`}>
                      {selectedUser.user.subscription_status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Progress */}
              <div>
                <h4 className="font-mono font-semibold text-white mb-3">Progress History</h4>
                {selectedUser.progress?.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {selectedUser.progress.map((p) => (
                      <div key={p.id} className="p-3 bg-vexl-bg border border-vexl-border">
                        {p.photo_url && (
                          <img
                           src={p.photo_url}
                           alt="Progress"
                           className="w-full h-32 object-cover mb-2"
                          />
                        )}
                        {p.weight && (
                          <p className="text-sm">
                            <span className="text-vexl-text-secondary">Weight:</span>{" "}
                            <span className="font-mono" style={{ color: brandColor }}>{p.weight} kg</span>
                          </p>
                        )}
                        <p className="text-xs text-vexl-text-secondary mt-1">
                          {new Date(p.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-vexl-text-secondary">No progress recorded yet</p>
                )}
              </div>

              {/* Nutrition Plan */}
              <div>
                <h4 className="font-mono font-semibold text-white mb-3">Nutrition Plan</h4>
                <div className="space-y-4">
                  <Input
                    placeholder="Plan name"
                    value={nutritionForm.name}
                    onChange={(e) => setNutritionForm({ ...nutritionForm, name: e.target.value })}
                    className="input-dark"
                  />
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs text-vexl-text-secondary">Calories</label>
                      <Input
                        type="number"
                        value={nutritionForm.calories}
                        onChange={(e) => setNutritionForm({ ...nutritionForm, calories: parseInt(e.target.value) })}
                        className="input-dark"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-vexl-text-secondary">Protein (g)</label>
                      <Input
                        type="number"
                        value={nutritionForm.protein}
                        onChange={(e) => setNutritionForm({ ...nutritionForm, protein: parseInt(e.target.value) })}
                        className="input-dark"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-vexl-text-secondary">Carbs (g)</label>
                      <Input
                        type="number"
                        value={nutritionForm.carbs}
                        onChange={(e) => setNutritionForm({ ...nutritionForm, carbs: parseInt(e.target.value) })}
                        className="input-dark"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-vexl-text-secondary">Fats (g)</label>
                      <Input
                        type="number"
                        value={nutritionForm.fats}
                        onChange={(e) => setNutritionForm({ ...nutritionForm, fats: parseInt(e.target.value) })}
                        className="input-dark"
                      />
                    </div>
                  </div>
                  <Textarea
                    placeholder="Notes for the user..."
                    value={nutritionForm.notes}
                    onChange={(e) => setNutritionForm({ ...nutritionForm, notes: e.target.value })}
                    className="input-dark"
                  />
                  <Button
                    type="button"
                    onClick={saveNutritionPlan}
                    className="bg-vexl-primary text-vexl-bg hover:bg-vexl-primary-hover"
                  >
                    Save Nutrition Plan
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InfluencerDashboard;
